import { useEffect, useState } from 'react';
import { api, type PlatformScores, type TenantScore } from '../api.ts';
import { AccountBadge } from '../ClientRow.tsx';
import { asDate } from '../tenant/format.ts';
import { message } from './Platform.tsx';
import { ScoreDefinitionNote, ScoreFigure, ScoreRange } from './ScoreParts.tsx';
import {
  NOT_YET_ASSESSED,
  compareScores,
  isAssessed,
  percentileText,
  rankText,
  spreadValue,
} from './scores.ts';

/**
 * Severity-weighted scores across the whole platform — staff only (ADR-0081).
 *
 * This screen exists inside a product that refuses scores (ADR-0007), and it is
 * built so that the refusal's reasons still hold here: every figure sits over
 * the counts it was made from, the definition is printed above the tables
 * rather than behind a link, and a tenant nobody has assessed says so instead
 * of scoring zero. The platform area as a whole is unreachable to a customer
 * login; nothing on this screen is repeated anywhere a customer can see.
 */

type TenantSort = 'score' | 'name';

export const Scores = () => {
  const [scores, setScores] = useState<PlatformScores | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<TenantSort>('score');
  const [ascending, setAscending] = useState(false);

  useEffect(() => {
    void api
      .platformScores()
      .then(setScores)
      .catch((cause: unknown) => setError(message(cause)));
  }, []);

  if (error !== null) return <p className="error-banner">{error}</p>;
  if (scores === null) return <p className="note">Loading scores…</p>;

  const { frameworks, platform, definition } = scores;

  const tenants = [...scores.tenants].sort((a, b) =>
    sort === 'name'
      ? (ascending ? 1 : -1) * a.displayName.localeCompare(b.displayName)
      : compareScores(a.overall.score, b.overall.score, ascending ? 'ascending' : 'descending'),
  );

  const onSort = (key: TenantSort) => {
    // A new column starts in its natural direction: best score first, A to Z.
    setAscending((previous) => (key === sort ? !previous : key === 'name'));
    setSort(key);
  };

  const ariaSort = (key: TenantSort) =>
    key !== sort ? 'none' : ascending ? 'ascending' : 'descending';

  return (
    <section className="pf-stack">
      <ScoreDefinitionNote definition={definition} />

      <div className="card">
        <div className="card-head">
          <h2>Across the platform</h2>
        </div>
        <div className="card-body">
          <p className="note sc-panel-lead">
            Over each live tenant’s latest completed assessment. Tenants with nothing assessed are
            left out of these figures, not counted as zero.
          </p>
          <div className="tbl-wrap">
            <table className="tbl sc-table">
              <thead>
                <tr>
                  <th scope="col">Scope</th>
                  <th scope="col">Tenants scored</th>
                  <th scope="col">Minimum</th>
                  <th scope="col">Median</th>
                  <th scope="col">Average</th>
                  <th scope="col">Maximum</th>
                  <th scope="col">Range</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { id: 'overall', name: 'Overall', spread: platform.overall },
                  ...frameworks.map((framework) => ({
                    id: framework.id,
                    name: framework.name,
                    spread: platform.frameworks[framework.id],
                  })),
                ].map((line) => (
                  <tr key={line.id}>
                    <th scope="row" className="sc-name">
                      {line.name}
                    </th>
                    <td className="num">{line.spread.tenants}</td>
                    <td className="num">{spreadValue(line.spread.min)}</td>
                    <td className="num">{spreadValue(line.spread.median)}</td>
                    <td className="num">{spreadValue(line.spread.average)}</td>
                    <td className="num">{spreadValue(line.spread.max)}</td>
                    <td>
                      <ScoreRange spread={line.spread} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Clients ({scores.accounts.length})</h2>
        </div>
        <div className="card-body">
          <p className="note sc-panel-lead">
            An account’s score pools the weights of all its tenants rather than averaging their
            scores, so a barely-read tenant counts for less than a fully-read one.
          </p>
          <div className="tbl-wrap">
            <table className="tbl sc-table">
              <thead>
                <tr>
                  <th scope="col">Client</th>
                  <th scope="col">Tenants</th>
                  <th scope="col">Overall</th>
                  <th scope="col">Rank</th>
                  <th scope="col">Percentile</th>
                  {frameworks.map((framework) => (
                    <th scope="col" key={framework.id}>
                      {framework.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scores.accounts.map((account) => (
                  <tr key={account.accountId}>
                    <th scope="row" className="sc-name">
                      {account.accountName}
                    </th>
                    <td className="num">{account.tenants}</td>
                    <td>
                      <ScoreFigure cell={account.overall} />
                    </td>
                    <td className="num">{rankText(account.position)}</td>
                    <td className="num">{percentileText(account.position)}</td>
                    {frameworks.map((framework) => (
                      <td key={framework.id}>
                        <ScoreFigure cell={account.frameworks[framework.id]} short />
                      </td>
                    ))}
                  </tr>
                ))}
                {scores.accounts.length === 0 && (
                  <tr>
                    <td colSpan={5 + frameworks.length}>
                      <span className="note">No client accounts yet.</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Tenants ({scores.tenants.length})</h2>
        </div>
        <div className="card-body">
          <div className="tbl-wrap">
            <table className="tbl sc-table">
              <thead>
                <tr>
                  <th scope="col" aria-sort={ariaSort('name')}>
                    <button type="button" className="sc-sort" onClick={() => onSort('name')}>
                      Tenant {sortArrow(ariaSort('name'))}
                    </button>
                  </th>
                  <th scope="col">Last assessed</th>
                  <th scope="col" aria-sort={ariaSort('score')}>
                    <button type="button" className="sc-sort" onClick={() => onSort('score')}>
                      Overall {sortArrow(ariaSort('score'))}
                    </button>
                  </th>
                  <th scope="col">Rank</th>
                  <th scope="col">Percentile</th>
                  {frameworks.map((framework) => (
                    <th scope="col" key={framework.id}>
                      {framework.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <TenantLine key={tenant.tenantId} tenant={tenant} frameworks={frameworks} />
                ))}
                {tenants.length === 0 && (
                  <tr>
                    <td colSpan={5 + frameworks.length}>
                      <span className="note">No live tenants yet.</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

const sortArrow = (state: 'none' | 'ascending' | 'descending') => (
  <span aria-hidden="true">{state === 'none' ? '↕' : state === 'ascending' ? '↑' : '↓'}</span>
);

interface TenantLineProps {
  readonly tenant: TenantScore;
  readonly frameworks: PlatformScores['frameworks'];
}

const TenantLine = ({ tenant, frameworks }: TenantLineProps) => {
  const assessed = isAssessed(tenant);
  return (
    <tr>
      <th scope="row" className="sc-name">
        {tenant.displayName}
        <br />
        <AccountBadge name={tenant.accountName} />
      </th>
      <td className="num">{tenant.completedAt === null ? 'Never' : asDate(tenant.completedAt)}</td>
      {assessed ? (
        <>
          <td>
            <ScoreFigure cell={tenant.overall} />
          </td>
          <td className="num">{rankText(tenant.position)}</td>
          <td className="num">{percentileText(tenant.position)}</td>
          {frameworks.map((framework) => (
            <td key={framework.id}>
              <ScoreFigure cell={tenant.frameworks[framework.id]} short />
            </td>
          ))}
        </>
      ) : (
        <td colSpan={3 + frameworks.length}>
          <span className="note">{NOT_YET_ASSESSED}</span>
        </td>
      )}
    </tr>
  );
};
